import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Stack,
  Tooltip,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { format } from 'date-fns';
import { UserAvatar } from '@/components/common';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import type { WorkOrderNote, NoteCategory } from '@/types';
import { dateFnsLocale } from '@/utils/locale';

interface LocalNote {
  category: NoteCategory;
  content: string;
  authorName: string;
  createdAt: string;
}

interface NotesThreadProps {
  category: NoteCategory;
  notes: WorkOrderNote[];
  isLoading?: boolean;
  onSend: (content: string) => void;
  onDelete?: (noteId: string) => void;
  onUpdate?: (noteId: string, content: string) => void | Promise<void>;
  isSending?: boolean;
  isUpdating?: boolean;
  creationMode?: boolean;
  localNotes?: LocalNote[];
  onLocalNoteAdd?: (content: string) => void;
  inputHeight?: number | null;
}

const DEFAULT_INPUT_HEIGHT = 140;

export function NotesThread({
  notes,
  isLoading,
  onSend,
  onDelete,
  onUpdate,
  isSending,
  isUpdating,
  creationMode,
  localNotes,
  onLocalNoteAdd,
  inputHeight: inputHeightProp,
}: NotesThreadProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const user = useAuthStore((s) => s.user);
  const [message, setMessage] = useState('');
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentInputHeight = inputHeightProp ?? DEFAULT_INPUT_HEIGHT;

  const dateLocale = dateFnsLocale();

  // Scroll to bottom when new notes arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [notes.length, localNotes?.length]);

  const handleSend = () => {
    const trimmed = message.trim();
    if (!trimmed) return;

    if (creationMode && onLocalNoteAdd) {
      onLocalNoteAdd(trimmed);
    } else {
      onSend(trimmed);
    }
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleConfirmDelete = () => {
    if (deleteNoteId && onDelete) {
      onDelete(deleteNoteId);
    }
    setDeleteNoteId(null);
  };

  // In creation mode, show local notes
  const displayNotes: WorkOrderNote[] = creationMode
    ? (localNotes ?? []).map((ln, i) => ({
        id: `local-${i}`,
        bookingId: '',
        category: ln.category,
        content: ln.content,
        authorId: user?.id ?? '',
        authorName: ln.authorName,
        createdAt: ln.createdAt,
        updatedAt: null,
      }))
    : notes;

  const beginEdit = (note: WorkOrderNote) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  const cancelEdit = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const saveEdit = async () => {
    if (!editingNoteId || !onUpdate) return;
    const trimmed = editingContent.trim();
    if (!trimmed) return;
    await onUpdate(editingNoteId, trimmed);
    setEditingNoteId(null);
    setEditingContent('');
  };

  const isOwn = (note: WorkOrderNote) => note.authorId === user?.id;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Messages area */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minHeight: 100,
        }}
      >
        {displayNotes.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('notesHistory.noNotes')}
            </Typography>
          </Box>
        ) : (
          // Reverse to show oldest first (API returns desc)
          [...displayNotes].reverse().map((note) => {
            const own = isOwn(note);
            const authorName = note.authorName?.trim() ?? '';
            const canModify =
              !creationMode && (own || useAuthStore.getState().hasRole(['manager', 'owner', 'admin']));
            const isEditing = editingNoteId === note.id;
            return (
              <Box
                key={note.id}
                sx={{
                  display: 'flex',
                  flexDirection: own ? 'row-reverse' : 'row',
                  alignItems: 'flex-end',
                  gap: 1,
                }}
              >
                {!own && (
                  <UserAvatar
                    name={note.authorName ?? '?'}
                    size={28}
                  />
                )}
                <Box sx={{ maxWidth: '75%' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: own ? 'primary.main' : 'action.hover',
                      color: own ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      borderBottomRightRadius: own ? 4 : undefined,
                      borderBottomLeftRadius: own ? undefined : 4,
                    }}
                  >
                    {(authorName || own) && (
                      <Typography
                        variant="caption"
                        color="inherit"
                        data-testid="note-author"
                        sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}
                      >
                        {authorName}
                        {own && (
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 400,
                              opacity: 0.75,
                              ...(authorName ? { ml: 0.5 } : {}),
                            }}
                          >
                            {tc('youParenthetical')}
                          </Box>
                        )}
                      </Typography>
                    )}
                    {isEditing ? (
                      <Stack spacing={0.5}>
                        <TextField
                          value={editingContent}
                          onChange={(e) => setEditingContent(e.target.value)}
                          multiline
                          fullWidth
                          autoFocus
                          size="small"
                          variant="standard"
                          slotProps={{
                            input: {
                              sx: {
                                color: 'inherit',
                                fontSize: '0.875rem',
                                lineHeight: 1.43,
                                p: 0,
                              },
                              disableUnderline: true,
                            },
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEdit();
                            } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              void saveEdit();
                            }
                          }}
                        />
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={tc('cancel')}>
                            <IconButton
                              size="small"
                              onClick={cancelEdit}
                              sx={{ p: 0.25, color: 'inherit' }}
                              aria-label={tc('cancel')}
                            >
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={tc('save')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => void saveEdit()}
                                disabled={!editingContent.trim() || isUpdating}
                                sx={{ p: 0.25, color: 'inherit' }}
                                aria-label={tc('save')}
                              >
                                {isUpdating ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <CheckIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="inherit" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                        {note.content}
                      </Typography>
                    )}
                  </Paper>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: own ? 'flex-end' : 'flex-start',
                      gap: 0.5,
                      mt: 0.25,
                      px: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      {format(new Date(note.createdAt), 'd.M. HH:mm', { locale: dateLocale })}
                      {note.updatedAt && ` · ${t('notesHistory.edited')}`}
                    </Typography>
                    {canModify && !isEditing && onUpdate && (
                      <Tooltip title={t('notesHistory.editNote')}>
                        <IconButton
                          size="small"
                          onClick={() => beginEdit(note)}
                          sx={{ p: 0.25 }}
                          aria-label={t('notesHistory.editNote')}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canModify && !isEditing && onDelete && (
                      <Tooltip title={t('notesHistory.deleteNote')}>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteNoteId(note.id)}
                          sx={{ p: 0.25 }}
                          aria-label={t('notesHistory.deleteNote')}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Spacer for dialog-level splitter overlay */}
      <Box sx={{ height: 8, flexShrink: 0 }} />

      {/* Input area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.5,
          height: currentInputHeight,
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          multiline
          size="small"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('notesHistory.writePlaceholder')}
          sx={{
            height: '100%',
            minWidth: 0,
            '& .MuiInput-root': {
              height: '100%',
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-inputMultiline': {
              boxSizing: 'border-box',
              height: '100% !important',
              overflow: 'auto !important',
            },
          }}
        />
        <Tooltip title={t('notesHistory.sendNote')}>
          <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!message.trim() || isSending}
              aria-label={t('notesHistory.sendNote')}
            >
              {isSending ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Tooltip>
      </Box>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteNoteId !== null}
        title={t('notesHistory.deleteNote')}
        message={t('notesHistory.deleteNoteConfirm')}
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteNoteId(null)}
      />
    </Box>
  );
}
